/**
 * The Training & Placement Cell — contacts, facilities, eligibility and how a
 * drive runs, from the institute's own page:
 * https://www.bietdvg.edu/placement/placement_dept
 *
 * Reproduced closely; only obvious typos are corrected ("Group Discussion Rooms"
 * was printed as a plural for a single room) and sentences are tightened where
 * the source runs two thoughts together. Nothing is added.
 *
 * Companion to placements.ts, which holds the drive-by-drive numbers. This file
 * is the qualitative half: who to call, what the campus can host, and what a
 * student or a recruiter actually has to do.
 */
import type { Media } from "@zivin-labs/site-content";

export interface CellContact {
  name: string;
  /** Staff-directory slug to borrow a portrait from — verified exact matches. */
  staffSlug?: string;
  role: string;
  /** Second line as published, where there is one. */
  detail?: string;
  phone: string;
  email: string;
}

/**
 * All three share placement@bietdvg.edu — that is the address the institute
 * publishes for the Cell, and it replaces the invented "tpo@bietdvg.edu" that
 * used to appear on the recruiter page.
 */
export const PLACEMENT_CONTACTS: CellContact[] = [
  {
    name: "Dr. Nirmala C R",
    staffSlug: "nirmala-c-r",
    role: "Dean, Placement",
    detail: "Professor and Head, Department of CS&E",
    phone: "+91 97407 70377",
    email: "placement@bietdvg.edu",
  },
  {
    name: "Prof. Supreeth",
    staffSlug: "supreeth",
    role: "Placement Officer",
    detail: "Department of Training & Placement",
    phone: "+91 78994 17070",
    email: "placement@bietdvg.edu",
  },
];

export interface Facility {
  /** The number, pulled out so it can be set as a figure. */
  figure: string;
  label: string;
}

/** What the campus can host during a recruitment drive. */
export const PLACEMENT_FACILITIES: Facility[] = [
  { figure: "700", label: "Seats in the auditorium" },
  { figure: "250", label: "Seats in the placement seminar hall" },
  { figure: "50", label: "Seats in the conference meeting hall" },
  { figure: "600+", label: "Updated systems across the computer labs" },
  { figure: "300", label: "Students who can sit an online test in one slot" },
  { figure: "1 + 5", label: "Group-discussion room and personal interview rooms" },
];

/**
 * What a student needs to clear to sit a drive — the Cell's "Typical Eligibility
 * Norms" as supplied 2026-09-04.
 *
 * This revised the thresholds the page carried before: the general bar was
 * 6.75 CGPA and the higher one 7.75, now 6 and 7. A fourth line about a
 * written-test exemption in the TCS Ninja drive at 7.75 CGPA was dropped with
 * them — it is absent from the new norms and its figure contradicted them.
 */
export const ELIGIBILITY: string[] = [
  "The general threshold for most companies is a 60% aggregate, or 6 CGPA, across all semesters.",
  "Premium recruiters require 7 CGPA and above.",
  "Students must have no active backlogs; a handful of recruiters tolerate up to one.",
];

export interface ProcessStep {
  title: string;
  body: string;
}

/** How a drive reaches a student, in the Cell's own sequence. */
export const STUDENT_PROCESS: ProcessStep[] = [
  {
    title: "Data in the sixth semester",
    body: "Every student enters their details in the format supplied. The department coordinator validates their department's data and sends the sheet to the Cell, which consolidates all branches and updates it with each semester's result.",
  },
  {
    title: "The circular",
    body: "When a company confirms, the Cell issues a circular with the eligibility criteria and the essential details. It goes to every student and is posted on the department notice board.",
  },
  {
    title: "Register",
    body: "Eligible students are told to register and take part in the drive.",
  },
];

/** What happens on the day, as the Cell lists it. */
export const DRIVE_DAY: string[] = [
  "Pre-placement talk",
  "Aptitude test",
  "Group discussion, where the company holds one",
  "Interview — technical and HR",
  "Final selection announced",
];

/** The recruiter's side of the same process. */
export const RECRUITER_PROCESS: ProcessStep[] = [
  {
    title: "Invitations go out",
    body: "Placement activity is initiated in July and August each year, when the Cell sends invitations to all recruiting companies.",
  },
  {
    title: "Confirm and send the brief",
    body: "Interested companies return a participation confirmation with the prerequisites — a tentative drive date, eligibility criteria, job description and salary package.",
  },
  {
    title: "A date is allotted",
    body: "The Training & Placement department scrutinises the requirement, and campus recruitment dates are allotted to participating companies on mutual confirmation.",
  },
  {
    title: "Run the drive on campus",
    body: "Companies visit on the allotted dates and conduct tests and interviews as their own process requires. The Cell asks, where possible, that final results be announced on the day of recruitment.",
  },
];

export interface Venue {
  media: Media;
  title: string;
  caption: string;
}

/**
 * The four venues a drive actually runs through, from the institute's own
 * infrastructure gallery. Sixteen images were ingested to
 * sites/biet/placement/; these are the four that are both legible and clean.
 *
 * Deliberately not used:
 *   - Several lab shots carry the phone's own timestamp burnt into the corner
 *     ("2020/7/14 08:47 · REDMI NOTE 6 PRO"), which reads as a snapshot rather
 *     than a photograph of a facility.
 *   - The gallery files one interior under "Auditorium" that is plainly the
 *     guest waiting area — sofas and a coffee table. Left out rather than
 *     captioned as something it is not.
 *
 * All sixteen remain in the bucket if a fuller gallery is ever wanted.
 */
export const PLACEMENT_VENUES: Venue[] = [
  {
    media: {
      url: "sites/biet/placement/placement-hall-02.jpg",
      alt: "The placement seminar hall, tiered seating facing a projector screen and podium",
      width: 1280,
      height: 960,
    },
    title: "Placement seminar hall",
    caption: "250 seats, where the pre-placement talk is given.",
  },
  {
    media: {
      url: "sites/biet/placement/auditorium-05.jpg",
      alt: "The auditorium filled with students during a session",
      width: 2560,
      height: 1440,
    },
    title: "Auditorium",
    caption: "700 seats, for the drives that draw a whole cohort.",
  },
  {
    media: {
      url: "sites/biet/placement/computer-lab-01.jpg",
      alt: "Students at workstations in a computer lab",
      width: 2560,
      height: 1707,
    },
    title: "Computer labs",
    caption: "600+ systems; 300 candidates can sit an online test at once.",
  },
  {
    media: {
      url: "sites/biet/placement/board-room-01.jpg",
      alt: "The conference room, a single meeting table with seating around it",
      width: 1280,
      height: 960,
    },
    title: "Conference room",
    caption: "50 seats, for the panel and the debrief.",
  },
];

/**
 * A second, non-overlapping set for the Placements page, so the two pages do
 * not show the same four photographs. Same rooms, different frames — the
 * auditorium from the balcony rather than full of people, the seminar hall
 * empty rather than mid-talk.
 */
export const PLACEMENT_VENUES_ALT: Venue[] = [
  {
    media: {
      url: "sites/biet/placement/auditorium-06.jpg",
      alt: "The auditorium seen from the balcony, tiered seating facing the stage",
      width: 2560,
      height: 1707,
    },
    title: "The auditorium",
    caption: "Where a drive that draws the whole cohort is held.",
  },
  {
    media: {
      url: "sites/biet/placement/placement-hall-01.jpg",
      alt: "The placement seminar hall, empty, screen lowered over the podium",
      width: 1280,
      height: 960,
    },
    title: "The seminar hall",
    caption: "Where the pre-placement talk opens the day.",
  },
  {
    // Re-cropped from computer-lab-05: the original carried the phone's own
    // "REDMI NOTE 6 PRO · 2020/7/14 08:48" burnt into the bottom strip. Cutting
    // 310px (14%) clears it and drops a cluttered foreground desk with it.
    media: {
      url: "sites/biet/placement/computer-lab-rows.jpg",
      alt: "Rows of workstations in a computer lab, set out for an examination",
      width: 2560,
      height: 1242,
    },
    title: "The labs",
    caption: "Where the aptitude test is sat.",
  },
];
